import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { HttpApiResponseInterface } from 'pf-frontend-common/src/module/http/api-response.interface';

import { BackendConfigSettingsInterface } from 'common/data/backend/config/settings.interface';
import { LocationModel } from 'common/data/location/model';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { settingsGetPageType } from 'common/helper/settings/get-page-type';
import { AutocompleteResultInterface } from 'common/module/autocomplete/result.interface';
import { AutocompleteViewStoreEvent } from 'common/module/autocomplete/view-store.event';
import { AutocompleteViewStoreInterface } from 'common/module/autocomplete/view-store.interface';
import { DataStore } from 'common/module/data/store';
import { InputSelectOptionInterface } from 'common/module/input/select/option.interface';
import { InputStateInterface } from 'common/module/input/state.interface';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorNumberRange } from 'common/module/input/validator/number-range';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';
import { StatsGtmProviderInterface } from 'common/module/stats/gtm/provider.interface';
import { BuildingReviewStoreServiceInterface } from 'common/service/building-review-store/service.interface';
import { CountryStoreServiceInterface } from 'common/service/country-store/service.interface';
import { BuildingReviewAutocompleteStoreEvent } from 'desktop/module/building-review/autocomplete/store.event';
import { BuildingReviewAutocompleteStoreDecoratorInterface } from 'desktop/module/building-review/autocomplete/store-decorator.interface';
import { BuildingReviewFormInputInterface } from 'desktop/module/building-review/form/input.interface';
import { BuildingReviewFormInputAdapter } from 'desktop/module/building-review/form/input-adapter';
import { BuildingReviewFormModelAdapter } from 'desktop/module/building-review/form/model-adapter';
import { buildingReviewFormStatsGtmSubmit } from 'desktop/module/building-review/form/stats/gtm/submit';
import { BuildingReviewFormTemplatePropsInterface } from 'desktop/module/building-review/form/template-props.interface';
import { BuildingReviewFormActionType, BuildingReviewFormRatingType } from 'desktop/module/building-review/form/types';
import { BuildingReviewFormViewStoreEvent } from 'desktop/module/building-review/form/view-store.event';
import { BuildingReviewFormViewStoreOptionsInterface } from 'desktop/module/building-review/form/view-store-options.interface';

export class BuildingReviewFormViewStore extends DataStore {
  /**
   * Description text minimum number of characters
   */
  public static readonly DESCRIPTION_MIN_LENGTH: number = 75;

  /**
   * Minimum rating score
   */
  public static readonly RATING_MIN: number = 1;

  /**
   * Maximum rating score
   */
  public static readonly RATING_MAX: number = 5;

  /**
   * @inheritDoc
   */
  protected state: BuildingReviewFormTemplatePropsInterface;

  /**
   * Form has autocomplete
   */
  protected hasAutocomplete: boolean = false;

  /**
   * Terms and conditions checked
   */
  protected isTermsChecked: boolean = true;

  /**
   * Countries list
   */
  protected countriesList: InputSelectOptionInterface[] = [];

  /**
   * Constructor
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected buildingReviewStoreService: BuildingReviewStoreServiceInterface,
    protected countryStoreService: CountryStoreServiceInterface,
    protected autocompleteViewStore: AutocompleteViewStoreInterface,
    protected autocompleteStore: BuildingReviewAutocompleteStoreDecoratorInterface,
    protected settings: BackendConfigSettingsInterface,
    protected statsGtm: StatsGtmProviderInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    const descMinLength = BuildingReviewFormViewStore.DESCRIPTION_MIN_LENGTH;
    const descPlaceholder = i18nTranslate('Write your review - Tell people about your experience (Optional)');
    const ratingMin = BuildingReviewFormViewStore.RATING_MIN;
    const ratingMax = BuildingReviewFormViewStore.RATING_MAX;
    const ratingError = i18nTranslate('Value must be between {range-from} and {range-to}.');

    /**
     * Generate stars array of index => value
     */
    const stars = [];
    for (let i = ratingMin; i <= ratingMax; i++) {
      stars.push(i);
    }

    this.state = {
      action: '',
      stars,
      hasAutocomplete: this.hasAutocomplete,
      isTermsChecked: this.isTermsChecked,
      ratings: [
        'rating_maintenance',
        'rating_staff_and_security',
        'rating_pool_and_gym',
        'rating_children_friendly',
        'rating_noise_in_and_around',
        'rating_traffic_near',
        'rating_guest_parking',
      ],
      location: new LocationModel(),
      autocompleteViewStore: this.autocompleteViewStore,
      input: {
        rating_maintenance: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Maintenance'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_staff_and_security: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Staff / Security'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_pool_and_gym: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Gym / pool'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_children_friendly: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Children friendly'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_noise_in_and_around: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Noise in and around property'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_traffic_near: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Traffic near property'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        rating_guest_parking: {
          value: 0,
          valueDisplayed: 0,
          placeholder: i18nTranslate('Guest parking'),
          validators: [new InputValidatorNumberRange(ratingError, ratingMin, ratingMax)],
        },
        title: {
          value: '',
          validators: [],
          placeholder: i18nTranslate('Title your review (Optional)'),
        },
        location: {
          value: '',
          validators: [new InputValidatorRequired(i18nTranslate('Please select a building'))],
        },
        text: {
          value: '',
          validators: [],
          placeholder: descPlaceholder,
          minLength: descMinLength,
          currentLength: 0,
        },
        reviewer_name: {
          value: '',
          validators: [new InputValidatorRequired(i18nTranslate('Please enter your name'))],
          placeholder: i18nTranslate('Your name'),
        },
        reviewer_email: {
          value: '',
          validators: [new InputValidatorEmail(i18nTranslate('Please enter a valid email'), true)],
          placeholder: i18nTranslate('Your email (Optional)'),
        },
        terms: {
          value: true,
          validators: [new InputValidatorRequired(i18nTranslate('Please agree to terms and conditions'))],
        },
      },
      onChangeInput: this.onChangeInput,
      onKeyUpInput: this.onKeyUpInput,
      onInputText: this.onInputText,
      onClickSubmit: this.onClickSubmit,
      onClickClose: this.onClickClose,
      onMouseOverStars: this.onMouseOverStars,
      onMouseLeaveStars: this.onMouseLeaveStars,
      onClickStars: this.onClickStars,
      onClickSelectAnotherBuilding: this.onClickSelectAnotherBuilding,
      onClickAcceptTerms: this.onClickAcceptTerms,
    };
  }

  /**
   * Reset state (useful if form should be resubmitted)
   */
  public resetState(): void {
    // Set default state
    this.initState();

    // Clear autocomplete
    this.autocompleteViewStore.clear();
  }

  /**
   * Initialize view store state
   */
  public initialize(options: BuildingReviewFormViewStoreOptionsInterface): void {
    this.hasAutocomplete = options.hasAutocomplete;

    // Reset state
    this.resetState();

    this.initAutocomplete();
  }

  /**
   * @inheritDoc
   */
  public getState(): BuildingReviewFormTemplatePropsInterface {
    return this.state;
  }

  /**
   * Update location data
   */
  public updateLocation(id: string, name: string): void {
    const locationModel = new LocationModel();

    locationModel.id = id;
    locationModel.name = name;

    const state: BuildingReviewFormTemplatePropsInterface = {
      ...this.state,
      location: locationModel,
    };

    this.setState(state, this.updateLocation);
    if (this.state.input.location) {
      this.updateInput('location', locationModel.id, false);
    }
  }

  /**
   * Update input data
   *
   * @param name  - Input name
   * @param value - New value
   */
  public updateInput(
    name: keyof BuildingReviewFormInputInterface,
    value: string | number | boolean,
    validate: boolean = true
  ): void {
    const state: BuildingReviewFormTemplatePropsInterface = {
      ...this.state,
    };

    // Get state input
    const input = state.input[name];

    // Input not found
    if (!input) {
      return;
    }

    // Update input
    input.value = value;
    if (validate) {
      input.error = this.validateInput(input);
    }

    this.setState(state, this.updateInput);
  }

  /**
   * Update action
   */
  public updateAction(action: BuildingReviewFormActionType): void {
    this.setState({
      ...this.state,
      action,
    });
  }

  /**
   * Update text counter
   */
  public updateTextCounter(value: number): void {
    // Clone state
    const state = {
      ...this.state,
    };

    // Update text current length
    state.input.text.currentLength = value;

    // Refresh state
    this.setState(state, this.updateTextCounter);
  }

  /**
   * Init autocomplete
   */
  protected initAutocomplete(): void {
    this.autocompleteViewStore.setPlaceholder(i18nTranslate('Search Building'));

    this.autocompleteViewStore
      .getEventEmitter()
      .addListener(AutocompleteViewStoreEvent.keyUp, this.onKeyUpAutocomplete);

    this.autocompleteStore
      .getEventEmitter()
      .addListener(BuildingReviewAutocompleteStoreEvent.searchSuccess, this.onSearchSuccessAutocompleteStore);

    this.autocompleteViewStore
      .getEventEmitter()
      .addListener(AutocompleteViewStoreEvent.selectResultItem, this.onSelectResultItemAutocompleteViewStore);
  }

  /**
   * Validate the input and returns error message if any
   */
  protected validateInput(input: InputStateInterface): string {
    // Input has validators
    if (input.validators) {
      let value = input.value;
      if (typeof value === 'string') {
        value = value.trim();
      }

      const errors: string[] = input.validators
        .map((validator: InputValidatorInterface) => validator.getError(value, this.state.input))
        .filter((message: string) => !!message);
      // Only keep first error if any
      return errors[0] || '';
    }

    return '';
  }

  /**
   * Process form submit
   */
  protected processSubmit(): void {
    // Validate all inputs
    const state: BuildingReviewFormTemplatePropsInterface = {
      ...this.state,
    };

    const errors = Object.keys(this.state.input)
      .map((name: keyof BuildingReviewFormInputInterface) => {
        // Get state input
        const input = this.state.input[name];

        // Update input
        input.error = this.validateInput(input);

        return input;
      })
      .filter((input: InputStateInterface) => input.error);
    // Update form state
    this.setState(state, this.onClickSubmit);

    // Form is valid
    if (errors.length === 0) {
      this.submit();
    }
  }

  /**
   * Submit the form to the API
   */
  protected submit(): void {
    // Submit state action
    this.updateAction('loading');

    // Current state
    const state = this.getState();

    // Convert form data to modal
    const buildingReviewModel = new BuildingReviewFormModelAdapter(state.input).getData();

    // Send API request
    this.buildingReviewStoreService
      .create(state.location.id, buildingReviewModel)
      .then((response: HttpApiResponseInterface) => {
        // Success state action
        this.updateAction('send-success');
        this.statsGtm.send(buildingReviewFormStatsGtmSubmit(settingsGetPageType(this.settings)));
      })
      .catch((response: HttpApiResponseInterface) => {
        // Validation failed
        if (response.status === 400) {
          // Back to default state action
          this.updateAction('');

          // Convert response & input state into new input state
          const input = new BuildingReviewFormInputAdapter(response, this.state.input).getData();

          // Update state
          this.setState({
            ...this.state,
            input,
          });

          // Exit
          return;
        }

        // Server error state action
        this.updateAction('send-error');
      });
  }

  /**
   * Changed input
   */
  protected onChangeInput = (e: Event): void => {
    const input = <HTMLInputElement>e.currentTarget;

    this.updateInput(
      <keyof BuildingReviewFormInputInterface>input.name,
      input.name === 'terms' ? input.checked : input.value
    );
  };

  /**
   * Key up on input
   */
  protected onKeyUpInput = (e: KeyboardEvent): void => {
    if (e.keyCode === KeyboardCharcodeEnum.enter) {
      this.processSubmit();

      return;
    }

    const input = <HTMLInputElement>e.currentTarget;

    this.updateInput(
      <keyof BuildingReviewFormInputInterface>input.name,
      input.name === 'terms' ? input.checked : input.value
    );
  };

  /**
   * Autocomplete keyup event handler
   */
  protected onKeyUpAutocomplete = () => {
    const autocompleteState = this.autocompleteViewStore.getState();

    this.updateLocation('', '');

    if (!this.autocompleteViewStore.getState().searchString) {
      this.autocompleteViewStore.setAutocompleteResults([]);
      return;
    }

    this.autocompleteStore.search(autocompleteState.searchString);
  };

  /**
   * Autocomplete select result item event handler
   */
  protected onSelectResultItemAutocompleteViewStore = (e: Event, result: AutocompleteResultInterface) => {
    this.autocompleteViewStore.setSearchString(result.text);
    this.autocompleteViewStore.setInputValue(result.text);
    this.autocompleteViewStore.toggleClearButtonVisibility(false);

    this.updateLocation(result.id, result.text);
  };

  /**
   * Triggers when autocomplete search succeded
   */
  protected onSearchSuccessAutocompleteStore = () => {
    const results = this.autocompleteStore.getAutocompleteResults() || [];

    this.autocompleteViewStore.setAutocompleteResults(results);

    const autocompleteState = this.autocompleteViewStore.getState();

    this.autocompleteViewStore.toggle(
      !!autocompleteState.autocompleteResults.length && !!this.autocompleteViewStore.getState().searchString
    );
  };

  /**
   * On text input
   */
  protected onInputText = (e: Event): void => {
    const input = <HTMLInputElement>e.currentTarget;

    this.updateTextCounter(input.value.length);

    if (this.state.input.text.error) {
      this.updateInput(<keyof BuildingReviewFormInputInterface>input.name, input.value);
    }
  };

  /**
   * Clicked submit button
   */
  protected onClickSubmit = (e: MouseEvent): void => {
    this.processSubmit();
  };

  /**
   * Clicked close button
   */
  protected onClickClose = (e: MouseEvent): void => {
    this.eventEmitter.emit(BuildingReviewFormViewStoreEvent.close);
  };

  /**
   * Mouse over stars container
   *
   * @param name  - Input name
   * @param value - Input value
   */
  protected onMouseOverStars = (name: BuildingReviewFormRatingType, value: number): void => {
    // Clone state
    const state = {
      ...this.state,
    };

    // Update only displayed value, keep original value
    state.input[name].valueDisplayed = value;

    // Reset any error message
    state.input[name].error = '';

    // Update state
    this.setState(state);
  };

  /**
   * Clicked stars container
   *
   * @param name  - Input name
   * @param value - Input value
   */
  protected onClickStars = (name: BuildingReviewFormRatingType, value: number): void => {
    // Clone state
    const state = {
      ...this.state,
    };

    // Update value & displayed value
    state.input[name].value = value;
    state.input[name].valueDisplayed = state.input[name].value;

    // Reset any error message
    state.input[name].error = '';

    // Update state
    this.setState(state);
  };

  /**
   * Mouse leave stars container
   *
   * @param name  - Input name
   * @param value - Input value
   */
  protected onMouseLeaveStars = (name: BuildingReviewFormRatingType, value: number): void => {
    // Clone state
    const state = {
      ...this.state,
    };

    // Restore original value
    state.input[name].valueDisplayed = state.input[name].value;

    // Update state
    this.setState(state);
  };

  /**
   * Click select another building
   */
  protected onClickSelectAnotherBuilding = (): void => {
    this.hasAutocomplete = true;
    this.setState({
      ...this.state,
      hasAutocomplete: this.hasAutocomplete,
    });
  };

  /**
   * Click select another building
   */
  protected onClickAcceptTerms = (): void => {
    this.setState({
      ...this.state,
      isTermsChecked: !this.state.isTermsChecked,
    });
  };
}
